(() => {
  if (window.customElements.get('beni-announcement-bar')) return;

  const HEIGHT_PROPERTY = '--beni-announcement-bar-height';
  const COLLAPSE_DURATION = 400;

  // SLOP：低于此位移不判定方向，让轻微抖动的点击仍然是点击。
  // THRESHOLD：判定为「一次滑动」所需的横向距离，取值偏小是因为公告栏很矮。
  const SWIPE_SLOP = 6;
  const SWIPE_THRESHOLD = 30;

  // 带显式时区的串（结尾是 Z 或 ±HH:MM / ±HHMM）本身就指向唯一的绝对时刻，
  // 解析结果与访客所在地无关，可以放心当 epoch 用。
  // 要求偏移量前面确实是 HH:MM，是为了排除 '12-31-2026' 这类以 -YYYY 结尾、
  // 形似偏移量的日期串（'-20' + '26' 会被误判成 -20:26）。
  const ABSOLUTE_TIME = /\d{2}:\d{2}(?::\d{2})?(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})$/;

  // 时间来源只认绝对时刻，绝不按访客本地时区解释裸墙上时钟：
  // 配置里的 2026-12-31 23:59:59 不带时区，交给 new Date() 只能按【访客本地时区】猜，
  // 与服务端的店铺时区差出「店铺偏移 − 访客偏移」，跨时区访客会提前 / 延后被裁掉。
  //
  // 首选服务端按店铺时区折算好的 data-*-epoch（秒），与 Date.now() 同基准；
  // 它为 0 说明 Liquid 没解析出这个格式，此时只接受自带时区的原始串，
  // 其余一律返回 0 —— 调用方的 > 0 守卫会跳过裁剪。
  const resolveEpoch = (epochSeconds, rawWallClock) => {
    const epoch = Number(epochSeconds || 0);
    if (epoch > 0) return epoch * 1000;

    const raw = (rawWallClock || '').trim();
    if (!ABSOLUTE_TIME.test(raw)) return 0;

    // '2026-12-31 23:59:59-08:00' 这种空格分隔的写法不是合规 ISO，部分浏览器解析不了，
    // 补成 T 分隔再交给 Date（已经带 T 的串不含空格，replace 是空操作）。
    const parsed = new Date(raw.replace(' ', 'T')).getTime();

    return Number.isNaN(parsed) ? 0 : parsed;
  };

  class BeniAnnouncementBar extends HTMLElement {
    constructor() {
      super();
      this.resizeObserver = null;
      this.rotationTimer = null;
      this.scrollFrame = null;
      this.slideIndex = 0;
      this.isPaused = false;
      this.isScrolledPastHero = false;
      // 展开态下量到的真实条高，供 handleScroll 计算迟滞区宽度。
      // 收起后 offsetHeight 归零，量不到真实值，故在展开时缓存下来。
      this.expandedHeight = 0;
      this.swipe = null;
      this.handleClose = this.handleClose.bind(this);
      this.handleTextLink = this.handleTextLink.bind(this);
      this.handleTextLinkKeydown = this.handleTextLinkKeydown.bind(this);
      this.handlePointerDown = this.handlePointerDown.bind(this);
      this.handlePointerMove = this.handlePointerMove.bind(this);
      this.handlePointerEnd = this.handlePointerEnd.bind(this);
      this.handleScroll = this.handleScroll.bind(this);
      this.handleBlockExpired = this.handleBlockExpired.bind(this);
      this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
      this.pauseRotation = this.pauseRotation.bind(this);
      this.resumeRotation = this.resumeRotation.bind(this);
    }

    connectedCallback() {
      if (this.classList.contains('is-closed')) {
        this.setHeight(0);
        return;
      }

      // 兜底 Shopify 整页缓存：服务端渲染时可能尚未到期，送达浏览器时已过期。
      this.pruneStaleSlides();

      if (!this.slides.length) {
        this.remove();
        return;
      }

      // 关闭按钮每条公告各一个（跟随内容排在最右），因此用事件委托统一接管：
      // 逐个绑定的话，slide 被 pruneStaleSlides / handleBlockExpired 移除后还得逐个解绑。
      this.addEventListener('click', this.handleClose);
      this.addEventListener('click', this.handleTextLink);
      this.addEventListener('keydown', this.handleTextLinkKeydown);
      this.addEventListener('beni:announcement-block-expired', this.handleBlockExpired);

      this.observeHeight();
      this.setupRotation();

      if (this.dataset.displayMode === 'hide_after_hero') {
        window.addEventListener('scroll', this.handleScroll, { passive: true });
        this.handleScroll();
      }
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClose);
      this.removeEventListener('click', this.handleTextLink);
      this.removeEventListener('keydown', this.handleTextLinkKeydown);
      this.removeEventListener('beni:announcement-block-expired', this.handleBlockExpired);
      this.removeEventListener('mouseenter', this.pauseRotation);
      this.removeEventListener('mouseleave', this.resumeRotation);
      this.removeEventListener('focusin', this.pauseRotation);
      this.removeEventListener('focusout', this.resumeRotation);
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
      window.removeEventListener('scroll', this.handleScroll);

      // 手势进行中被移除（编辑器重渲染）时，window 上的 move / up 监听必须一并摘掉。
      this.removeEventListener('pointerdown', this.handlePointerDown);
      window.removeEventListener('pointermove', this.handlePointerMove);
      window.removeEventListener('pointerup', this.handlePointerEnd);
      window.removeEventListener('pointercancel', this.handlePointerEnd);
      this.swipe = null;

      if (this.scrollFrame) {
        window.cancelAnimationFrame(this.scrollFrame);
        this.scrollFrame = null;
      }

      this.stopRotation();
      this.resizeObserver?.disconnect();
      this.resizeObserver = null;

      // 编辑器重新渲染 section 时会先 disconnect 再 connect：只有真正从文档移除才归零高度，
      // 否则新节点的 ResizeObserver 尚未触发前 header 会短暂贴到 0。
      if (!document.querySelector('beni-announcement-bar')) this.setHeight(0);
    }

    get slides() {
      return Array.from(this.querySelectorAll('.beni-announcement-bar__slide'));
    }

    pruneStaleSlides() {
      const now = Date.now();

      this.slides.forEach((slide) => {
        const start = resolveEpoch(slide.dataset.startEpoch, slide.dataset.start);
        const end = resolveEpoch(slide.dataset.endEpoch, slide.dataset.end);
        const isPending = start > 0 && start > now;
        const isExpired = end > 0 && end <= now && slide.dataset.endAction === 'hide_block';

        if (isPending || isExpired) slide.remove();
      });

      this.syncActiveSlide();
    }

    // 保证恰有一条 is-active（服务端渲染的首条被裁掉时也能自愈）。
    syncActiveSlide() {
      const slides = this.slides;
      if (!slides.length) return;

      if (this.slideIndex >= slides.length) this.slideIndex = 0;

      slides.forEach((slide, index) => {
        slide.classList.toggle('is-active', index === this.slideIndex);
      });

      this.syncHostColors(slides[this.slideIndex]);
    }

    // 配色在 block 上，靠 slide 的 inline style 向下继承。唯一继承不到的是整条底色：
    // 它挂在宿主元素上（淡入淡出时两条重叠，需要一层不透明兜底），因此切条时同步过来。
    // 关闭按钮已移入 slide 内部，颜色直接继承，无需在此处理。
    syncHostColors(slide) {
      if (!slide) return;

      const background = slide.dataset.barBackground;
      if (!background) return;

      this.style.setProperty('--beni-announcement-bar-bg', background);

      // #shopify-section-xxx 的底色是收起动画期间的兜底层，一并跟上。
      if (this.parentElement) this.parentElement.style.background = background;
    }

    setHeight(height) {
      // 不吸顶模式：公告栏随页滚走，不占据顶部空间，预留高度恒为 0。
      // 在此统一拦截，observeHeight / ResizeObserver / collapse 等各调用点都不必各自判断。
      const value = this.dataset.displayMode === 'not_sticky' ? 0 : height;
      document.documentElement.style.setProperty(HEIGHT_PROPERTY, `${Math.round(value)}px`);
    }

    // 文案换行、字体加载、图片解码都会改变条高，统一由 ResizeObserver 校正。
    observeHeight() {
      this.setHeight(this.offsetHeight);
      this.expandedHeight = this.offsetHeight;

      if (typeof ResizeObserver === 'undefined') return;

      this.resizeObserver = new ResizeObserver(() => {
        if (this.classList.contains('is-hidden') || this.classList.contains('is-closed')) return;
        // 展开动画期间 max-height 被 inline 钉住，高度交给 expand() 收尾，避免互相打架。
        if (this.style.maxHeight) return;
        this.setHeight(this.offsetHeight);
        this.expandedHeight = this.offsetHeight;
      });

      this.resizeObserver.observe(this);
    }

    // 收起：把 max-height 从实测高度过渡到 0，整条真实塌陷（不是位移），
    // 外层 section 高度随之归零，header 才不会被公告栏盖住。
    collapse() {
      if (this.classList.contains('is-hidden')) return;

      this.style.maxHeight = `${this.offsetHeight}px`;
      void this.offsetHeight;

      this.classList.add('is-hidden');
      this.style.maxHeight = '0px';
      this.setHeight(0);
      this.stopRotation();
    }

    expand() {
      if (!this.classList.contains('is-hidden')) return;

      this.classList.remove('is-hidden');
      this.style.maxHeight = '';
      const target = this.offsetHeight;

      this.style.maxHeight = '0px';
      void this.offsetHeight;
      this.style.maxHeight = `${target}px`;
      this.setHeight(target);

      const release = () => {
        if (this.classList.contains('is-hidden')) return;
        this.style.maxHeight = '';
        this.setHeight(this.offsetHeight);
      };

      this.onceCollapseTransitionEnd(release);
      this.startRotation();
    }

    // 只认本元素自身的 max-height 过渡：轮播淡入淡出的 opacity 过渡会从 slide 冒泡到这里，
    // 若不过滤会提前触发回调（展开时高度提早解锁、关闭时提早 display: none）。
    // 同时挂一个定时器兜底 —— transition 被 prefers-reduced-motion 压缩或被打断时 transitionend 不会来。
    onceCollapseTransitionEnd(callback) {
      let settled = false;

      const finish = () => {
        if (settled) return;
        settled = true;
        this.removeEventListener('transitionend', onTransitionEnd);
        callback();
      };

      const onTransitionEnd = (event) => {
        if (event.target !== this || event.propertyName !== 'max-height') return;
        finish();
      };

      this.addEventListener('transitionend', onTransitionEnd);
      window.setTimeout(finish, COLLAPSE_DURATION);
    }

    get rotationDelay() {
      const delay = Number(this.dataset.autoplay || 0);
      return delay > 0 ? delay : 0;
    }

    setupRotation() {
      this.syncActiveSlide();

      if (this.slides.length < 2) return;

      this.addEventListener('mouseenter', this.pauseRotation);
      this.addEventListener('mouseleave', this.resumeRotation);
      this.addEventListener('focusin', this.pauseRotation);
      this.addEventListener('focusout', this.resumeRotation);
      document.addEventListener('visibilitychange', this.handleVisibilityChange);

      // 只有两条以上才接管手势：单条时保持原生行为，链接点击、文本选中都不受干扰。
      this.addEventListener('pointerdown', this.handlePointerDown);

      this.startRotation();
    }

    // 手动左右滑动切换。用 Pointer Events 一套代码同时覆盖触屏与鼠标拖拽
    handlePointerDown(event) {
      // 只响应主键 / 单指；右键与多指手势交还浏览器。
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      if (this.swipe) return;

      this.swipe = {
        id: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        deltaX: 0,
        // 竖向意图一旦确立就整段放弃，避免把页面滚动误判成翻页。
        abandoned: false,
        moved: false
      };

      // 绑在 window 上：手指 / 光标滑出公告栏范围后仍能收到 move 与 up，
      // 否则会留下一个永不结束的 swipe 状态。
      window.addEventListener('pointermove', this.handlePointerMove, { passive: true });
      window.addEventListener('pointerup', this.handlePointerEnd);
      window.addEventListener('pointercancel', this.handlePointerEnd);
    }

    handlePointerMove(event) {
      const swipe = this.swipe;
      if (!swipe || event.pointerId !== swipe.id || swipe.abandoned) return;

      const deltaX = event.clientX - swipe.startX;
      const deltaY = event.clientY - swipe.startY;

      // 方向锁：越过阈值的那一刻谁大听谁的，之后不再改判。
      if (!swipe.moved && Math.abs(deltaX) < SWIPE_SLOP && Math.abs(deltaY) < SWIPE_SLOP) return;

      if (!swipe.moved) {
        if (Math.abs(deltaY) > Math.abs(deltaX)) {
          swipe.abandoned = true;
          return;
        }

        swipe.moved = true;
        this.classList.add('is-swiping');
        // 拖拽期间停掉自动轮播，否则手还没松开就自己跳走了。
        this.pauseRotation();
      }

      swipe.deltaX = deltaX;
    }

    handlePointerEnd(event) {
      const swipe = this.swipe;
      if (!swipe || (event.pointerId !== undefined && event.pointerId !== swipe.id)) return;

      window.removeEventListener('pointermove', this.handlePointerMove);
      window.removeEventListener('pointerup', this.handlePointerEnd);
      window.removeEventListener('pointercancel', this.handlePointerEnd);

      this.swipe = null;
      this.classList.remove('is-swiping');

      const deltaX = swipe.deltaX || 0;
      const isSwipe = swipe.moved && !swipe.abandoned && Math.abs(deltaX) >= SWIPE_THRESHOLD;

      if (isSwipe) {
        // 左滑（deltaX < 0）看下一条，右滑看上一条。
        this.goTo(this.slideIndex + (deltaX < 0 ? 1 : -1));

        // 拖拽结束时浏览器仍会补派一个 click：不吞掉的话，
        // 在链接上滑动会顺带跳转。捕获阶段只拦这一次。
        this.suppressNextClick();
      }

      // 恢复自动轮播。鼠标拖拽是例外：光标很可能仍悬停在公告栏上，
      // 而 mouseenter 本就要求暂停，这里若无条件恢复会把悬停暂停取消掉 ——
      // 交给 mouseleave 去恢复。触屏 / 触控笔没有悬停概念，松手即恢复。
      if (swipe.moved && event.pointerType !== 'mouse') this.resumeRotation();
    }

    suppressNextClick() {
      const swallow = (clickEvent) => {
        clickEvent.preventDefault();
        clickEvent.stopPropagation();
      };

      this.addEventListener('click', swallow, { capture: true, once: true });

      window.requestAnimationFrame(() => {
        this.removeEventListener('click', swallow, { capture: true });
      });
    }

    startRotation() {
      this.stopRotation();

      if (this.isPaused || document.hidden) return;
      if (this.slides.length < 2 || !this.rotationDelay) return;
      if (this.classList.contains('is-hidden') || this.classList.contains('is-closed')) return;

      this.rotationTimer = window.setInterval(() => this.goTo(this.slideIndex + 1), this.rotationDelay);
    }

    stopRotation() {
      if (this.rotationTimer === null) return;
      window.clearInterval(this.rotationTimer);
      this.rotationTimer = null;
    }

    pauseRotation() {
      this.isPaused = true;
      this.stopRotation();
    }

    resumeRotation() {
      this.isPaused = false;
      this.startRotation();
    }

    handleVisibilityChange() {
      if (document.hidden) {
        this.stopRotation();
      } else {
        this.startRotation();
      }
    }

    goTo(index) {
      const slides = this.slides;
      if (slides.length < 2) return;

      this.slideIndex = ((index % slides.length) + slides.length) % slides.length;
      this.syncActiveSlide();
    }

    handleScroll() {
      if (this.scrollFrame) return;

      this.scrollFrame = window.requestAnimationFrame(() => {
        this.scrollFrame = null;

        // 已被用户关闭时不再接管显隐，避免滚回顶部又把它拉回来。
        if (this.classList.contains('is-closed')) return;

        // 迟滞（hysteresis）：收起会把 section 真实高度塌到 0，scrollY 随之回退一个条高；
        // 若收 / 展共用一个阈值，收起后的 scrollY 会立刻跌回阈值另一侧，导致反复横跳。
        // 因此展开阈值比收起阈值低「一个条高 + 余量」，让回退量落在死区内。
        // 收起态量不到条高（offsetHeight 为 0），改用展开时缓存的 expandedHeight。
        const barHeight = this.classList.contains('is-hidden')
          ? this.expandedHeight
          : this.offsetHeight;
        const hideThreshold = window.innerHeight;
        const showThreshold = window.innerHeight - barHeight - 8;
        const y = window.scrollY;

        if (!this.isScrolledPastHero && y > hideThreshold) {
          this.isScrolledPastHero = true;
          this.collapse();
        } else if (this.isScrolledPastHero && y < showThreshold) {
          this.isScrolledPastHero = false;
          this.expand();
        }
      });
    }

    handleClose(event) {
      // 委托：只响应关闭按钮上的点击，公告内的链接点击不受影响。
      if (event && !event.target.closest('.beni-announcement-bar__close')) return;

      this.collapse();

      // 主题编辑器里不写入关闭记录，配合首屏内联脚本的同款判断，
      // 保证运营在编辑器中点了关闭后，刷新/切换页面仍能看到公告栏。
      const isDesignMode = !!(window.Shopify && window.Shopify.designMode);

      if (!isDesignMode) {
        try {
          window.localStorage.setItem(this.dataset.storageKey, this.dataset.shopToday);
        } catch (error) {
          // localStorage 不可用时，本次会话内仍然关闭，只是无法跨页记住。
        }
      }

      // 收起动画结束后才 display: none，避免直接消失。
      this.onceCollapseTransitionEnd(() => {
        this.classList.add('is-closed');
        this.setHeight(0);
      });
    }

    // 运营只填了 Link URL、没填链接文案时，整段文案区域承担跳转。
    // 不用 <a> 包裹是因为富文本里本就允许写链接，嵌套 <a> 会被浏览器拆开。
    handleTextLink(event) {
      const text = event.target.closest?.('.beni-announcement-bar__text[data-text-link]');
      if (!text) return;

      // 富文本自带的链接、关闭按钮等交互元素保持各自行为。
      if (event.target.closest('a, button')) return;

      // 拖拽选中文案后松手不该跳转（单条公告时不走 swipe 那套抑制逻辑）。
      const selection = window.getSelection();
      if (selection && !selection.isCollapsed && text.contains(selection.anchorNode)) return;

      const url = text.dataset.textLink;
      if (!url) return;

      if (event.metaKey || event.ctrlKey) {
        window.open(url, '_blank', 'noopener');
      } else {
        window.location.href = url;
      }
    }

    handleTextLinkKeydown(event) {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      if (!event.target.closest?.('.beni-announcement-bar__text[data-text-link]')) return;

      // 空格默认会滚动页面。
      event.preventDefault();
      this.handleTextLink(event);
    }

    handleBlockExpired(event) {
      const blockId = event.detail?.blockId;
      if (!blockId) return;

      const slide = this.querySelector(`.beni-announcement-bar__slide[data-block-id="${blockId}"]`);
      if (!slide) return;

      const wasActive = slide.classList.contains('is-active');
      const removedIndex = this.slides.indexOf(slide);
      slide.remove();

      if (!this.slides.length) {
        this.setHeight(0);
        this.remove();
        return;
      }

      if (wasActive || removedIndex < this.slideIndex) {
        this.slideIndex = Math.max(0, this.slideIndex - (removedIndex < this.slideIndex ? 1 : 0));
      }

      this.syncActiveSlide();
      this.startRotation();
      this.setHeight(this.offsetHeight);
    }
  }

  window.customElements.define('beni-announcement-bar', BeniAnnouncementBar);
})();
